// Pure formatting utilities extracted from page orchestrators
// Used by Request, Settings, Scan, and other UI consumers

import type { FiatCurrencyCode } from '@shared/types/currency.ts';
import { formatFiatText } from '@shared/registry/fiat.ts';

export const formatCountdown = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0
    ? `${minutes}:${seconds.toString().padStart(2, '0')}`
    : `${seconds}s`;
};

export const buildQrLabel = (
  fiatAmount: string,
  fiatCurrency: FiatCurrencyCode,
  cryptoAmount: string | null,
  cryptoSymbol: string | null,
): string => {
  const fiatLabel = formatFiatText(fiatAmount, fiatCurrency);
  if (cryptoAmount !== null && cryptoSymbol !== null) {
    return `${fiatLabel}  ≈  ${cryptoAmount} ${cryptoSymbol}`;
  }
  return fiatLabel;
};

export const truncateAddress = (address: string, prefixLen: number, suffixLen: number): string =>
  address.length <= prefixLen + suffixLen + 3
    ? address
    : `${address.slice(0, prefixLen)}...${address.slice(-suffixLen)}`;

// --- Manual entry amount formatting ---
// For when users need to type a crypto amount into their wallet manually.
// Rounds UP (ceiling) to protect against underpayment.

export type ManualEntryFormat = Readonly<{
  readonly full: string;
  readonly rounded: string;
  readonly hint: string | null;
}>;

export const formatManualEntryAmount = (
  amount: string,
  sigDigits = 4,
): ManualEntryFormat => {
  const full = amount;
  const num = parseFloat(amount);

  if (!Number.isFinite(num) || num <= 0) {
    return Object.freeze({ full, rounded: amount, hint: null });
  }

  // Numbers >= 1: ceiling to sigDigits decimal places
  if (num >= 1) {
    const factor = Math.pow(10, sigDigits);
    const rounded = (Math.ceil(num * factor) / factor).toFixed(sigDigits);
    return Object.freeze({ full, rounded, hint: null });
  }

  // Numbers < 1: count leading zeros after decimal, keep sigDigits significant
  const decimalPart = amount.includes('.') ? amount.split('.')[1] ?? '' : '';
  let leadingZeros = 0;
  for (const ch of decimalPart) {
    if (ch === '0') leadingZeros++;
    else break;
  }

  const sigPart = decimalPart.slice(leadingZeros);
  let digits: string;

  if (sigPart.length <= sigDigits) {
    digits = sigPart.padEnd(sigDigits, '0');
  } else {
    // Ceiling: increment if any remaining digits are non-zero
    const truncated = parseInt(sigPart.slice(0, sigDigits), 10);
    const remainder = sigPart.slice(sigDigits);
    const hasRemainder = [...remainder].some((c) => c !== '0');
    const ceiled = hasRemainder ? truncated + 1 : truncated;
    digits = ceiled.toString();

    // Handle carry (e.g., 9999 + 1 = 10000 → one fewer leading zero)
    if (digits.length > sigDigits) {
      leadingZeros = Math.max(0, leadingZeros - 1);
      digits = digits.slice(0, sigDigits);
    } else {
      digits = digits.padStart(sigDigits, '0');
    }
  }

  const rounded = `0.${'0'.repeat(leadingZeros)}${digits}`;

  // Build human-readable hint for small decimals (2+ leading zeros)
  let hint: string | null = null;
  if (leadingZeros >= 2) {
    hint = `${leadingZeros} zeroes, then ${digits}`;
  } else if (leadingZeros === 1) {
    hint = `1 zero, then ${digits}`;
  }

  return Object.freeze({ full, rounded, hint });
};

// Base units → exact decimal string, trailing zeros trimmed (1025000n, 6 → "1.025"). Client-side twin
// of the server conversion formatter (that module pulls in server DB code, so the UI can't import it).
export const formatBaseUnits = (baseUnits: bigint, decimals: number): string => {
  if (decimals === 0) return baseUnits.toString();
  const s = baseUnits.toString().padStart(decimals + 1, '0');
  const frac = s.slice(-decimals).replace(/0+$/, '');
  return frac.length > 0 ? `${s.slice(0, -decimals)}.${frac}` : s.slice(0, -decimals);
};
